import { useState } from "react";
import {
  useInfohubAdminSearchSessionsQuery,
  useInfohubGetSessionMessagesQuery,
} from "../../../lib/apollo/graphql/infohub/infohub-generated";

export interface ChatSession {
  id: string;
  userId: string;
  userPhone: string;
  title: string;
  createdAt: string;
  messageCount: number;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

function useChatHistoryContainer() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDatePreset, setSelectedDatePreset] = useState("ALL");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isUserHistoryModalOpen, setIsUserHistoryModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(
    null
  );
  const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);

  const ITEMS_PER_PAGE = 20;

  // 날짜 변환 함수: 선택한 날짜를 API 형식으로 변환
  // startDate: 선택한 날짜의 전날 15:00:00.000Z
  // endDate: 선택한 날짜의 14:59:59.999Z
  const convertDateForAPI = (dateString: string, isStartDate: boolean): string => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (isStartDate) {
      // 전날로 설정
      date.setDate(date.getDate() - 1);
      date.setUTCHours(15, 0, 0, 0);
    } else {
      // 같은 날 14:59:59.999로 설정
      date.setUTCHours(14, 59, 59, 999);
    }

    return date.toISOString();
  };

  // 세션 목록 조회
  const { loading, data, refetch } = useInfohubAdminSearchSessionsQuery({
    variables: {
      query: searchText || undefined,
      userId: userFilter || undefined,
      startDate: convertDateForAPI(startDate, true) || undefined,
      endDate: convertDateForAPI(endDate, false) || undefined,
      first: ITEMS_PER_PAGE,
      after: afterCursor,
    },
    fetchPolicy: "network-only",
  });

  // 세션 메시지 조회 (상세보기용)
  const { data: messagesData, loading: messagesLoading } =
    useInfohubGetSessionMessagesQuery({
      variables: {
        sessionId: selectedSessionId || "",
      },
      skip: !selectedSessionId,
      fetchPolicy: "network-only",
    });

  // 세션 데이터 변환
  const sessions: ChatSession[] =
    data?.adminSearchSessions?.edges?.map((edge) => ({
      id: edge.node.id,
      userId: edge.node.userId,
      userPhone: edge.node.userId, // TODO: userPhone 필드가 추가되면 수정
      title: edge.node.title || "제목 없음",
      createdAt: edge.node.createdAt,
      messageCount: edge.node.messageCount,
    })) || [];

  // 메시지 데이터 변환
  const messages: ChatMessage[] =
    messagesData?.messages?.map((msg) => ({
      role: msg.role.toLowerCase() as "user" | "assistant",
      content: msg.content,
      createdAt: msg.createdAt,
    })) || [];

  // 특정 사용자의 세션 목록 (필터링)
  const userSessions = selectedUserId
    ? sessions.filter((session) => session.userId === selectedUserId)
    : [];

  const totalCount = data?.adminSearchSessions?.totalCount || 0;
  const hasNextPage = data?.adminSearchSessions?.pageInfo?.hasNextPage || false;
  const hasPreviousPage =
    data?.adminSearchSessions?.pageInfo?.hasPreviousPage || false;

  // 날짜 포맷팅
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  }

  // 날짜 프리셋 선택 핸들러
  function handleDatePresetSelect(preset: string) {
    setSelectedDatePreset(preset);

    let startDateValue = "";
    let endDateValue = "";

    if (preset === "ALL") {
      startDateValue = "";
      endDateValue = "";
    } else if (preset === "CUSTOM") {
      // 직접선택은 날짜 입력 필드 활성화만
      return;
    } else {
      const today = new Date();
      endDateValue = today.toISOString().split("T")[0];

      switch (preset) {
        case "TODAY":
          startDateValue = endDateValue;
          break;
        case "3DAYS":
          startDateValue = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
          break;
        case "7DAYS":
          startDateValue = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
          break;
        case "1MONTH":
          startDateValue = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
          break;
        case "6MONTHS":
          startDateValue = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
          break;
        case "1YEAR":
          startDateValue = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0];
          break;
      }
    }

    setStartDate(startDateValue);
    setEndDate(endDateValue);

    // 프리셋 선택 시 자동으로 검색 실행
    setCurrentPage(1);
    setAfterCursor(undefined);
    refetch({
      query: searchText || undefined,
      userId: userFilter || undefined,
      startDate: convertDateForAPI(startDateValue, true) || undefined,
      endDate: convertDateForAPI(endDateValue, false) || undefined,
      first: ITEMS_PER_PAGE,
      after: undefined,
    });
  }

  // 검색 핸들러
  function handleSearch() {
    setCurrentPage(1);
    setAfterCursor(undefined);
    refetch({
      query: searchText || undefined,
      userId: userFilter || undefined,
      startDate: convertDateForAPI(startDate, true) || undefined,
      endDate: convertDateForAPI(endDate, false) || undefined,
      first: ITEMS_PER_PAGE,
      after: undefined,
    });
  }

  // 초기화 핸들러
  function handleReset() {
    setSearchText("");
    setUserFilter("");
    setStartDate("");
    setEndDate("");
    setSelectedDatePreset("ALL");
    setCurrentPage(1);
    setSelectedUserId(null);
    setAfterCursor(undefined);
    refetch({
      query: undefined,
      userId: undefined,
      startDate: undefined,
      endDate: undefined,
      first: ITEMS_PER_PAGE,
      after: undefined,
    });
  }

  // 페이지 변경
  function handlePageChange(page: number) {
    setCurrentPage(page);

    if (page > currentPage && hasNextPage) {
      // 다음 페이지
      const endCursor = data?.adminSearchSessions?.pageInfo?.endCursor;
      setAfterCursor(endCursor || undefined);
      refetch({
        query: searchText || undefined,
        userId: userFilter || undefined,
        startDate: convertDateForAPI(startDate, true) || undefined,
        endDate: convertDateForAPI(endDate, false) || undefined,
        first: ITEMS_PER_PAGE,
        after: endCursor || undefined,
      });
    } else if (page < currentPage && hasPreviousPage) {
      // 이전 페이지 (현재 구현은 간단한 방식, 필요시 개선)
      setAfterCursor(undefined);
      refetch({
        query: searchText || undefined,
        userId: userFilter || undefined,
        startDate: convertDateForAPI(startDate, true) || undefined,
        endDate: convertDateForAPI(endDate, false) || undefined,
        first: ITEMS_PER_PAGE,
        after: undefined,
      });
    }
  }

  // 사용자 내역 조회
  function handleUserHistoryClick(userId: string) {
    setSelectedUserId(userId);
    setIsUserHistoryModalOpen(true);
  }

  // 사용자 내역 모달 닫기
  function handleCloseUserHistoryModal() {
    setIsUserHistoryModalOpen(false);
    setSelectedUserId(null);
  }

  // 세션 상세보기
  function handleSessionDetailClick(session: ChatSession) {
    setSelectedSession(session);
    setSelectedSessionId(session.id);
    setIsDetailModalOpen(true);
  }

  // 모달 닫기
  function handleCloseDetailModal() {
    setIsDetailModalOpen(false);
    setSelectedSession(null);
    setSelectedSessionId(null);
  }

  // 대략적인 총 페이지 수 계산 (cursor 기반이므로 정확하지 않을 수 있음)
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return {
    models: {
      sessions,
      userSessions,
      messages,
      loading: loading || messagesLoading,
      currentPage,
      totalPages,
      totalCount,
      searchText,
      userFilter,
      startDate,
      endDate,
      selectedDatePreset,
      selectedUserId,
      isDetailModalOpen,
      isUserHistoryModalOpen,
      selectedSession,
    },
    operations: {
      formatDate,
      handleSearch,
      handleReset,
      handlePageChange,
      handleUserHistoryClick,
      handleCloseUserHistoryModal,
      handleSessionDetailClick,
      handleCloseDetailModal,
      handleDatePresetSelect,
      setSearchText,
      setUserFilter,
      setStartDate,
      setEndDate,
    },
  };
}

export default useChatHistoryContainer;
